import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";
import Config from "@/common/config";
import router, { RouteNames } from "@/router";
import { useAuthStore } from "@/stores/auth";
import { useNotifications } from "@/common/notification";
import i18n from "@/localization/i18n";
import { debounce } from "lodash-es";

const setupInterceptorsOnInstance = (
  axiosInstance: AxiosInstance,
): AxiosInstance => {
  axiosInstance.interceptors.response.use(undefined, (error: AxiosError) => {
    switch (error.response?.status) {
      case 401:
        // 401: Unauthorized -> The token has expired or the user has logged out.
        // Make sure they are really logged out and redirect them to the login page.

        // If the 401 occurs when calling the logout endpoint, ignore it.
        // Otherwise this would lead to repeated calls to logout.
        //
        // When calling the login endpoint, 401 is returned for invalid credentials, thus also ignore it.
        if (
          !["login", "logout"].some((s) =>
            (error.request as XMLHttpRequest).responseURL.includes(s),
          )
        ) {
          handleInvalidSession();
        }
    }
    return Promise.reject(error);
  });

  axiosInstance.interceptors.request.use((request) => {
    const authStore = useAuthStore();

    if (request.headers["Authorization"] === undefined) {
      request.headers["Authorization"] = authStore.token
        ? `Bearer ${authStore.token}`
        : "";
    }

    return request;
  });

  return axiosInstance;
};

// When trying to open a page (e.g. the Dashboard) without a valid session, multiple requests may happen,
// that all cause a 401 response.
// Debounce the function to avoid handling the invalid session multiple times.
const handleInvalidSession = debounce(
  () => {
    useAuthStore()
      .performLogout()
      .then(() => {
        router.push({ name: RouteNames.LOGIN }).then(() => {
          useNotifications().push(
            "error",
            i18n.global.t("tokenExpiredError"),
            i18n.global.t("tokenExpiredErrorTitle"),
          );
        });
      });
  },
  2_000,
  { leading: true, trailing: false },
);

export const baseAxiosInstance = setupInterceptorsOnInstance(
  axios.create({
    baseURL: Config.apiEndpoints.baseURL,
    headers: {
      get: {
        Accept: "application/json",
      },
      post: {
        Accept: "application/json",
      },
    },
  }),
);

export const itrBaseAxiosInstance = setupInterceptorsOnInstance(
  axios.create({
    baseURL: Config.apiEndpoints.itrURL,
    headers: {
      get: {
        Accept: "application/json",
      },
      post: {
        Accept: "application/json",
      },
    },
  }),
);

export const shipmentTrackingAxiosInstance = axios.create({
  baseURL: Config.apiEndpoints.itrURL,
  headers: {
    get: {
      Accept: "application/json",
    },
    post: {
      "Content-Type": "application/json",
    },
  },
});

export const compassBaseAxiosInstance = setupInterceptorsOnInstance(
  axios.create({
    baseURL: Config.apiEndpoints.compassURL,
    headers: {
      get: {
        Accept: "application/json",
      },
      post: {
        Accept: "application/json",
      },
    },
  }),
);
